import React, { useState } from 'react';
import classNames from 'classnames';
import delay from '../../../common/utils/lib/delay';
import { useDispatch } from 'react-redux';

// Components
import useManageElementSelectionChanges from './hooks/useInboxManageElementSelectionChanges';
import useSheet from '../structural/sheet/hooks/useSheet';
import useScaledList from '../../canvas/zoom/list/useScaledList';

import SheetContainer, { CloseButton, Content, Header, Heading, NonDraggableArea, Sheet } from '../structural/sheet';
import InboxClosed from '../../inbox/InboxClosed';
import PoiBoardSectionContext from '../../components/pointsOfInterest/PoiBoardSectionContext';
import InboxScrollableList from '../../inbox/InboxScrollableList';
import { ZoomStateContextProvider } from '../../canvas/zoom/ZoomStateContext';

// Actions
import { finishEditingCurrentElement } from '../../element/selection/selectionActions';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { BoardSections } from '../../../common/boards/boardConstants';
import { SheetId } from '../structural/sheet/sheetTypes';
import { PlatformDetails } from '../../../common/platform/platformTypes';

import './MobileInbox.scss';

const STATE_SWITCH_DELAY_MS = 100;

type MobileInboxContainerProps = {
    currentBoard: ImMNElement;
    currentBoardId: string;
    gridSize: number;
    inboxElementIds: string[];
    isEditable: boolean;
    createShortcutFile: () => void;
    deselectAll: () => void;
    platformDetails: PlatformDetails;
};

const MobileInboxContainerContent = (props: MobileInboxContainerProps) => {
    const { platformDetails, gridSize } = props;

    const { zoomScale, listStyle } = useScaledList(platformDetails, gridSize);

    return (
        <NonDraggableArea>
            <ZoomStateContextProvider zoomScale={zoomScale}>
                <div className="inbox-scrollable-list-container">
                    <InboxScrollableList
                        {...props}
                        // @ts-ignore-next-line The props are correct, just confused by untyped decorators
                        initialListRenderSize={5}
                        listStyle={listStyle}
                    />
                </div>
            </ZoomStateContextProvider>
        </NonDraggableArea>
    );
};

const MobileInboxContainer = (props: MobileInboxContainerProps) => {
    const {
        inboxElementIds,
        currentBoardId,
        currentBoard,
        isEditable,

        deselectAll,
        createShortcutFile,
    } = props;

    const sheetProps = useSheet(SheetId.Inbox);
    const {
        isSheetOpen,
        activeSnapPoint: currentActiveSnapPoint,
        dispatchOpenSheet,
        dispatchCloseSheet,
        dispatchUpdateActiveSnapPoint,
    } = sheetProps;

    const [isClosedButtonVisible, setIsClosedButtonVisible] = useState(true);

    const dispatch = useDispatch();
    const dispatchFinishEditingElement = () => dispatch(finishEditingCurrentElement());

    const revealClosedButton = async () => {
        await delay(STATE_SWITCH_DELAY_MS);
        setIsClosedButtonVisible(true);
    };

    const openInboxSheet = async () => {
        setIsClosedButtonVisible(false);

        await delay(STATE_SWITCH_DELAY_MS);

        dispatchOpenSheet();
    };

    const closeInboxSheet = () => {
        dispatchCloseSheet();
        revealClosedButton();
    };

    const updateActiveSnapPoint = (activeSnapPoint: number) => {
        // If we're moving in between snap points, finish editing the current element to hide keyboard
        if (currentActiveSnapPoint !== 0) {
            dispatchFinishEditingElement();
        }

        dispatchUpdateActiveSnapPoint(activeSnapPoint);
    };

    useManageElementSelectionChanges(currentBoardId, isSheetOpen, deselectAll, closeInboxSheet);

    return (
        <>
            <InboxClosed
                className={classNames({
                    transitioned: true,
                    hidden: !isClosedButtonVisible,
                })}
                openInboxHandler={openInboxSheet}
                createShortcutFile={createShortcutFile}
                isEditable={isEditable}
                inboxElementIds={inboxElementIds}
                element={currentBoard}
            />

            <SheetContainer
                {...sheetProps}
                defaultSnapPoint={0.5}
                snapPoints={[0.5, 0.9]}
                className="MobileInbox"
                onCloseTransitionStart={revealClosedButton}
                dispatchUpdateActiveSnapPoint={updateActiveSnapPoint}
            >
                <Sheet>
                    <Header>
                        <Heading>Unsorted</Heading>
                        <CloseButton>Close</CloseButton>
                    </Header>

                    <PoiBoardSectionContext.Provider value={BoardSections.INBOX}>
                        <Content>
                            <MobileInboxContainerContent {...props} />
                        </Content>
                    </PoiBoardSectionContext.Provider>
                </Sheet>
            </SheetContainer>
        </>
    );
};

export default MobileInboxContainer;
